import { Box, Container, Typography, useTheme } from '@mui/material';
import {
  ConstantsNameDescriptions,
  ReportChallengeCard,
  ReportEncounterCard,
  ReportExerciseCard,
  ReportGoalCard,
} from '_api/types';
import { PrimaryButton, TextButton } from 'components/atoms';
import { PatientHeader } from 'components/atoms/typographyStyles';
import { PeopleCardView } from 'components/organisms/PeopleCardView';
import { PeopleDetailView } from 'components/organisms/PeopleDetailView';
import dayjs, { Dayjs } from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';

const filterByDateRange = (
  startDate: Dayjs,
  endDate: Dayjs,
  assignedDateStr: string,
  completedDateStr: string,
) => {
  const assignedDate = assignedDateStr ? dayjs(assignedDateStr) : null;
  const completedDate = completedDateStr ? dayjs(completedDateStr) : null;

  if (assignedDate == null) {
    return null;
  }

  if (completedDate == null && assignedDate.isSameOrBefore(endDate)) {
    return 'ACTIVE';
  }

  if (completedDate != null && completedDate.isSameOrBefore(endDate)) {
    return 'DONE';
  }

  return 'ACTIVE';
};

export const PatientEncounterReport = () => {
  const reportRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const theme = useTheme();
  const { reportData, startingDateStr, endingDateStr } = location.state || {};
  const startDate = dayjs(startingDateStr);
  const endDate = dayjs(endingDateStr);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const therapistName =
    reportData?.therapist?.firstName + ' ' + reportData?.therapist?.lastName;
  const diagnoses = reportData?.patient?.diagnosis
    .map((x: ConstantsNameDescriptions<string>) => x.description)
    .join(', ');
  const encounters = reportData?.encounters.map((enc: ReportEncounterCard) => {
    return {
      date: enc.date_of_observation,
      'time spent': enc.minutes + ' minutes',
      interaction: enc.interactionOccurred,
      type: enc.contactMethod,
    };
  });
  const totalTimeSpent = reportData?.encounters.reduce(
    (sum: number, enc: ReportEncounterCard) => sum + Number(enc.minutes),
    0,
  );
  const goals = reportData?.goals.map((goal: ReportGoalCard) => {
    return {
      name: goal.title,
      'goal type': goal.goalType,
      'latest rating': goal.latestRating,
    };
  });

  const filteredActiveExercises = reportData?.exercises.filter(
    (exercise: ReportExerciseCard) =>
      filterByDateRange(
        startDate,
        endDate,
        exercise.patientExerciseResponse.assignedDate,
        exercise.patientExerciseResponse.completedDate,
      ) === 'ACTIVE',
  );

  const filteredDoneExercises = reportData?.exercises.filter(
    (exercise: ReportExerciseCard) =>
      filterByDateRange(
        startDate,
        endDate,
        exercise.patientExerciseResponse.assignedDate,
        exercise.patientExerciseResponse.completedDate,
      ) === 'DONE',
  );

  const filteredExercises = [...filteredActiveExercises, ...filteredDoneExercises];
  const avgExerciseCompletion = reportData?.averageExerciseCompletion;

  const activeExerciseData = filteredActiveExercises.map(
    (exercise: ReportExerciseCard) => {
      const exerciseResponse = exercise.patientExerciseResponse;
      const assignedDate = exerciseResponse.assignedDate
        ? exerciseResponse.assignedDate
        : '';
      const formattedAssignedDate = assignedDate
        ? dayjs(assignedDate).format('MM/DD/YYYY')
        : '';
      const formattedStatus = `ACTIVE (${formattedAssignedDate})`;
      return {
        name: exerciseResponse.content.title.toUpperCase(),
        type: exerciseResponse.regimenType,
        speciality: exerciseResponse.therapyType?.description.toUpperCase() || 'N/A',
        'reps (sets x reps)': `${exerciseResponse.sets} x ${exerciseResponse.reps}`,
        'sessions (per day x per week)': `${exerciseResponse.perDay} x ${exerciseResponse.perWeek}`,
        'percent Complete': `${exercise?.percentageCompletion.toFixed(0)} %` || 'N/A',
        status: formattedStatus,
      };
    },
  );

  const doneExerciseData = filteredDoneExercises.map((exercise: ReportExerciseCard) => {
    const exerciseResponse = exercise.patientExerciseResponse;
    const assignedDate = exerciseResponse.assignedDate
      ? exerciseResponse.assignedDate
      : '';
    const formattedAssignedDate = assignedDate
      ? dayjs(assignedDate).format('MM/DD/YYYY')
      : '';
    const completedDate = exerciseResponse.completedDate
      ? exerciseResponse.completedDate
      : '';

    const formattedCompletedDate = completedDate
      ? dayjs(completedDate).format('MM/DD/YYYY')
      : '';
    const formattedStatus = `DONE (${formattedAssignedDate} - ${formattedCompletedDate})`;
    return {
      name: exerciseResponse.content.title.toUpperCase(),
      type: exerciseResponse.regimenType,
      speciality: exerciseResponse.therapyType?.description.toUpperCase() || 'N/A',
      'reps (sets x reps)': `${exerciseResponse.sets} x ${exerciseResponse.reps}`,
      'sessions (per day x per week)': `${exerciseResponse.perDay} x ${exerciseResponse.perWeek}`,
      'percent Complete': `${exercise?.percentageCompletion.toFixed(0)} %` || 'N/A',
      status: formattedStatus,
    };
  });

  const filteredActiveChallenges = reportData?.challenges.filter(
    (challenge: ReportChallengeCard) =>
      filterByDateRange(
        startDate,
        endDate,
        challenge.patientChallengeResponse.assignedDate,
        challenge.patientChallengeResponse.completedDate,
      ) === 'ACTIVE',
  );
  const filteredDoneChallenges = reportData?.challenges.filter(
    (challenge: ReportChallengeCard) =>
      filterByDateRange(
        startDate,
        endDate,
        challenge.patientChallengeResponse.assignedDate,
        challenge.patientChallengeResponse.completedDate,
      ) === 'DONE',
  );

  const filteredChallenges = [...filteredActiveChallenges, ...filteredDoneChallenges];
  const avgChallengeCompletion = reportData?.averageChallengeCompletion;

  const activeChallengeData = filteredActiveChallenges?.map(
    (challenge: ReportChallengeCard) => {
      const challengeResponse = challenge.patientChallengeResponse;
      const latestResultDate = challenge.latestResult ? challenge?.latestResultDate : '';
      const assignedDate = challengeResponse.assignedDate
        ? challengeResponse.assignedDate
        : '';
      const formattedAssignedDate = assignedDate
        ? dayjs(assignedDate).format('MM/DD/YYYY')
        : '';
      const formattedStatus = `ACTIVE (${formattedAssignedDate})`;
      return {
        name: challengeResponse.content.title.toUpperCase(),
        'per week': challengeResponse.perWeek,
        'latest result': challenge.latestResult
          ? `${challenge?.latestResult} ${challenge?.metric} (${dayjs(latestResultDate).format('MM/DD/YYYY')})`
          : 'N/A',
        status: formattedStatus,
      };
    },
  );

  const doneChallengeData = filteredDoneChallenges?.map(
    (challenge: ReportChallengeCard) => {
      const challengeResponse = challenge.patientChallengeResponse;
      const latestResultDate = challenge.latestResult ? challenge?.latestResultDate : '';
      const assignedDate = challengeResponse.assignedDate
        ? challengeResponse.assignedDate
        : '';
      const formattedAssignedDate = assignedDate
        ? dayjs(assignedDate).format('MM/DD/YYYY')
        : '';
      const completedDate = challengeResponse.completedDate
        ? challengeResponse.completedDate
        : '';
      const formattedCompletedDate = completedDate
        ? dayjs(completedDate).format('MM/DD/YYYY')
        : '';
      const formattedStatus = `DONE (${formattedAssignedDate} -${formattedCompletedDate})`;
      return {
        name: challengeResponse.content.title.toUpperCase(),
        'per week': challengeResponse.perWeek,
        'latest result': challenge.latestResult
          ? `${challenge?.latestResult} ${challenge?.metric} (${dayjs(latestResultDate).format('MM/DD/YYYY')})`
          : 'N/A',
        status: formattedStatus,
      };
    },
  );

  const therapistDetails = {
    title: 'Therapist',
    items: [
      { heading: 'Name', value: therapistName || 'N/A' },
      {
        heading: 'Role',
        value: reportData?.therapist?.therapistSpecializations?.length
          ? reportData.therapist.therapistSpecializations
              .map((spec: { description: string }) => spec.description)
              .join(', ')
          : 'N/A',
      },
      { heading: 'Hospital', value: reportData?.therapist?.hospitalName || 'N/A' },
      {
        heading: 'Total Time Spent',
        value: totalTimeSpent + ' minutes' || 'N/A',
      },
    ],
  };

  const patientDetails = {
    title: 'Patient Details',
    items: [
      {
        heading: 'Name',
        value:
          `${reportData?.patient?.firstName || 'N/A'} ${reportData?.patient?.lastName || ''}`.trim() ||
          'N/A',
      },
      { heading: 'Date of Birth', value: reportData?.patient?.dateOfBirth || 'N/A' },
      { heading: 'MRN', value: reportData?.patient?.mrn || 'N/A' },
      { heading: 'Diagnosis', value: diagnoses || 'N/A' },
      {
        heading: 'Episode Start Date',
        value: reportData?.patient?.creationDate || 'N/A',
      },
      {
        heading: 'App Interactions',
        value: reportData?.appInteractionCount || 'N/A',
      },
      {
        heading: 'Exercise Completion Rate',
        value: `${avgExerciseCompletion ? avgExerciseCompletion.toFixed(0) : 0} %`,
      },
      {
        heading: 'Challenge Completion Rate',
        value: `${avgChallengeCompletion ? avgChallengeCompletion.toFixed(0) : 0} %`,
      },
    ],
  };
  const EncountersCard = {
    title: 'Encounters',
    cards: encounters,
  };

  const exerciseCards = {
    title: 'Exercises',
    cards: [...activeExerciseData, ...doneExerciseData],
  };

  const FunctionalChallengesCards = {
    title: 'Functional Challenges',
    cards: [...activeChallengeData, ...doneChallengeData],
  };

  const goalsData = {
    title: 'Goals',
    cards: goals,
  };

  const handleDownloadPDF = () => {
    const reportElement = reportRef.current;
    if (reportElement) {
      const pageWidth = 210;
      // const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      // const contentHeight = pageHeight - (margin * 2);

      html2canvas(reportElement, {
        scale: 2,
        scrollY: -window.scrollY,
        backgroundColor: '#FFFFFF',
        logging: false,
      }).then((canvas) => {
        const imgWidth = contentWidth;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Scale ratio (pixels to mm)
        const scale = imgWidth / canvasWidth;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [210, canvasHeight * scale],
          compress: true,
        });

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const mmHeight = canvasHeight * scale; //sliceHeight * scale;

        pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, mmHeight);
        pdf.save(`PatientReport-${reportData?.patient?.lastName || 'Report'}.pdf`);
      });
    }
  };

  const onBack = () => {
    history.back();
  };

  return (
    <Container>
      <Box>
        <TextButton
          onClick={onBack}
          sx={{ color: theme.palette.primary.main, marginBottom: '10px', paddingLeft: 0 }}
        >
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '20px',
          color: 'black',
        }}
      >
        <PatientHeader style={{ margin: 0 }}>Remote Monitoring Encounter</PatientHeader>

        <Box>
          <PrimaryButton sx={{ padding: '10px' }} onClick={handleDownloadPDF}>
            Download Report
          </PrimaryButton>
        </Box>
      </Box>

      <Box ref={reportRef} style={{ padding: '20px', margin: '20px auto', width: '90%' }}>
        <Box
          sx={{
            display: 'flex',
            paddingTop: '15px',
            paddingLeft: '40px',
            alignItems: 'flex-start',
          }}
        >
          {endingDateStr && (
            <Typography>
              {startingDateStr} - {endingDateStr}
            </Typography>
          )}
          {!endingDateStr && <Typography>{startingDateStr}</Typography>}
        </Box>
        <PeopleDetailView data={therapistDetails} />
        <PeopleDetailView data={patientDetails} />
        {reportData?.encounters.length > 0 && (
          <PeopleCardView data={EncountersCard} rows={4} />
        )}
        {filteredExercises.length > 0 && <PeopleCardView data={exerciseCards} rows={3} />}
        {filteredChallenges.length > 0 && (
          <PeopleCardView data={FunctionalChallengesCards} rows={3} />
        )}
        {reportData?.goals.length > 0 && <PeopleCardView data={goalsData} rows={3} />}
      </Box>
    </Container>
  );
};
